import React from 'react'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../lib/ui/form'
import { Checkbox } from '~/lib/ui/checkbox'
import { Textarea } from '../../lib/ui/textarea'
import { Counter } from './Counter'

export const MultipleCheckboxesField = ({ label, name, options, control }) => {
  const fieldId = React.useId()
  const otherTextareaRef = React.useRef(null)
  const [otherAnnouncement, setOtherAnnouncement] = React.useState('')
  const [shouldFocusOther, setShouldFocusOther] = React.useState(false)

  React.useEffect(() => {
    if (!shouldFocusOther) return

    const frameId = requestAnimationFrame(() => {
      if (otherTextareaRef.current) {
        otherTextareaRef.current.focus()
      }
      setShouldFocusOther(false)
    })

    return () => cancelAnimationFrame(frameId)
  }, [shouldFocusOther])

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = field.value || { values: [], other: '' }
        const showOtherField = value.values?.includes('other') || false
        const descriptionId = `${fieldId}-help`
        const errorId = `${fieldId}-error`
        const otherTextId = `${fieldId}-other-text`
        const otherLabelId = `${fieldId}-other-label`
        const counterId = `${fieldId}-counter`
        const maxLength = 500
        const describedBy = fieldState.error
          ? `${descriptionId} ${errorId}`
          : descriptionId

        const handleCheckboxChange = (checked, optionValue) => {
          const currentValues = value.values || []
          const currentlyShowsOther = currentValues.includes('other')
          let newValues
          if (checked === true) {
            newValues = [...currentValues, optionValue]
          } else {
            newValues = currentValues.filter((v) => v !== optionValue)
          }

          const nextShowsOther = newValues.includes('other')
          if (!currentlyShowsOther && nextShowsOther) {
            setOtherAnnouncement(
              'Additional text input for Other is now visible.',
            )
            setShouldFocusOther(true)
          } else if (currentlyShowsOther && !nextShowsOther) {
            setOtherAnnouncement('Additional text input for Other hidden.')
          }

          field.onChange({ ...value, values: newValues })
        }
        const handleOtherChange = (e) => {
          field.onChange({ ...value, other: e.target.value })
        }
        return (
          <FormItem>
            <FormLabel id={`${fieldId}-label`}>{label}</FormLabel>
            <FormDescription
              id={descriptionId}
              className="mt-4 text-sm text-[#5d5d5d]"
            >
              Select all that apply
            </FormDescription>
            <div className="sr-only" aria-live="polite" role="status">
              {otherAnnouncement}
            </div>
            <FormControl
              aria-describedby={describedBy}
              aria-invalid={fieldState.invalid}
            >
              <div className="!mt-12">
                {options.map((option, idx) => {
                  const checkboxId = `${fieldId}-option-${idx}`
                  const checkboxLabelId = `${checkboxId}-label`

                  return (
                    <div
                      key={option.value}
                      className="mt-2 items-center space-x-2 space-y-0 rounded-md border border-[#b0b0b0] px-2 py-4 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 md:justify-center"
                    >
                      <div>
                        <Checkbox
                          id={checkboxId}
                          value={option.value}
                          checked={
                            value.values?.includes(option.value) || false
                          }
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(checked, option.value)
                          }
                          aria-describedby={describedBy}
                          aria-labelledby={checkboxLabelId}
                          aria-invalid={fieldState.invalid}
                          className="mr-2"
                        />
                        <label id={checkboxLabelId} htmlFor={checkboxId}>
                          {option.label}
                        </label>
                      </div>
                      {option.value === 'other' && showOtherField && (
                        <>
                          <label
                            htmlFor={otherTextId}
                            id={otherLabelId}
                            className="sr-only"
                          >
                            Other option details
                          </label>
                          <Textarea
                            ref={otherTextareaRef}
                            id={otherTextId}
                            placeholder="Your answer..."
                            className="mt-3 h-[114px] w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm sm:px-4 sm:py-3 sm:text-base"
                            value={value.other || ''}
                            onChange={handleOtherChange}
                            aria-labelledby={`${otherLabelId} ${fieldId}-label`}
                            aria-describedby={`${describedBy} ${counterId}`}
                            aria-invalid={fieldState.invalid}
                            maxLength={maxLength}
                          />
                          <div id={counterId} aria-live="polite">
                            <Counter
                              value={value.other || ''}
                              maxLength={maxLength}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </FormControl>
            <FormMessage id={errorId} aria-live="polite" />
          </FormItem>
        )
      }}
    />
  )
}
